
import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';
import Mark from 'mark.js';
import { useSelector } from 'react-redux';
import './keywordHighligter.css';
import DocumentViewer from '../DocumentViewer/DocumentViewer';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function KeywordHighlighter({ file }) {
  const keywords = useSelector((state) => state.Highlight.advSearchObj || []);

  const [fileType, setFileType] = useState(null);
  const [docxHtml, setDocxHtml] = useState('');
  const [numPages, setNumPages] = useState(null);

  const containerRef = useRef(null);

  /* -------------------- Helpers -------------------- */

  const getFileType = (file) => {
    if (!file || typeof file !== 'string') return null;
    const clean = file.split('?')[0].toLowerCase();
    if (clean.endsWith('.pdf')) return 'pdf';
    if (clean.endsWith('.docx')) return 'docx';
    if (clean.endsWith('.doc')) return 'doc';
    return 'html';
  };

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /* -------------------- Detect file change -------------------- */

  useEffect(() => {
    setFileType(getFileType(file));
    setDocxHtml('');
    setNumPages(null);
  }, [file]);

  /* -------------------- DOCX handling (SAFE) -------------------- */

  useEffect(() => {
    if (fileType !== 'docx' || !file) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(file);
        const arrayBuffer = await response.arrayBuffer();

        if (cancelled) return;

        const { value } = await mammoth.convertToHtml({ arrayBuffer });

        if (!cancelled) {
          setDocxHtml(value);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('DOCX parse error:', err);
          setDocxHtml('<p>Unable to preview this document.</p>');
        }
      }
    })();

    return () => {
      cancelled = true; // ✅ cancel async work on file change
    };
  }, [fileType, file]);

  /* -------------------- Highlight DOCX / HTML -------------------- */

  useEffect(() => {
    if (
      (fileType === 'docx' || fileType === 'html') &&
      containerRef.current &&
      keywords.length
    ) {
      const instance = new Mark(containerRef.current);
      instance.unmark({
        done: () => instance.mark(keywords),
      });
    }
  }, [fileType, keywords, docxHtml]);

  if (!fileType) return null;

  /* -------------------- Render -------------------- */

  return (
    <div className="keyword-highlighter">
      {/* ---------------- PDF ---------------- */}
      {fileType === 'pdf' && (
        <Document
          file={file}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          loading="Loading PDF..."
        >
          {Array.from({ length: numPages || 0 }, (_, index) => (
            <Page
              key={index}
              pageNumber={index + 1}
              renderTextLayer
              customTextRenderer={(textItem) => {
                if (!keywords.length) return textItem.str;

                const regex = new RegExp(
                  `(${keywords.map(escapeRegex).join('|')})`,
                  'gi'
                );

                return textItem.str.split(regex).map((part, i) =>
                  regex.test(part) ? (
                    <mark
                      key={i}
                      style={{
                        backgroundColor: 'yellow',
                        padding: '0 2px',
                      }}
                    >
                      {part}
                    </mark>
                  ) : (
                    part
                  )
                );
              }}
            />
          ))}
        </Document>
      )}

      {/* ---------------- DOCX ---------------- */}
      {fileType === 'docx' && (
        <div
          className="docx-content"
          ref={containerRef}
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      )}

      {/* ---------------- DOC (legacy) ---------------- */}
      {fileType === 'doc' && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '10px' }}>
            ⚠️ This document is in a legacy <strong>.doc</strong> format, so the
            Keyword highlighter will not work.
          </p>
          <DocumentViewer
            url={file}
            fileType={(() => {
              const cleanUrl = file?.split('?')[0] || '';
              return cleanUrl.split('.').pop()?.toLowerCase();
            })()}
          />
        </div>
      )}

      {/* ---------------- HTML ---------------- */}
      {fileType === 'html' && (
        <div ref={containerRef}>{typeof file === 'string' ? file : ''}</div>
      )}
    </div>
  );
}
